import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook that auto-scales content to fit within its container.
 * If content natural height exceeds available space, applies transform: scale()
 * so everything fits without scrolling.
 *
 * Only kicks in when content overflows by more than 5% — avoids
 * unnecessary micro-scaling on devices where layout already fits.
 */
// Echelle imposee par la largeur : 1 quand le contenu tient, et 1 aussi sous le
// plancher, ou l'on prefere laisser le manche defiler plutot que de rendre les
// notes trop petites pour etre cliquees.
export const resolveWidthRatio = (containerWidth, minContentWidth, minWidthScale) => {
  if (!(minContentWidth > 0) || !(containerWidth > 0)) return 1;
  const ratio = containerWidth / minContentWidth;
  return ratio >= 1 || ratio < minWidthScale ? 1 : ratio;
};

// Options (navigateur de bureau seulement) : minContentWidth est la largeur
// fixe du contenu (le manche ne retrecit pas), minWidthScale le plancher sous
// lequel on renonce a reduire et on laisse le manche defiler.
export const useAutoScale = (observeKey = null, { minContentWidth = 0, minWidthScale = 0.85 } = {}) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [fits, setFits] = useState(true);
  const [widthLimited, setWidthLimited] = useState(false);

  const updateScale = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const availableHeight = container.clientHeight;
    const containerWidth = container.clientWidth;
    if (availableHeight <= 0) return;

    // Contenu plus large que la zone : on le reduit comme le ferait un zoom du
    // navigateur, tant que l'echelle reste au-dessus du plancher.
    const widthRatio = resolveWidthRatio(containerWidth, minContentWidth, minWidthScale);

    // Temporarily reset scale AND width to measure natural height
    const prevTransform = content.style.transform;
    const prevWidth = content.style.width;
    const prevHeight = content.style.height;
    content.style.transform = 'scale(1)';
    content.style.width = `${100 / widthRatio}%`;
    content.style.height = 'auto';
    const naturalHeight = content.scrollHeight;
    content.style.transform = prevTransform;
    content.style.width = prevWidth;
    content.style.height = prevHeight;

    if (naturalHeight <= 0) return;

    const heightRatio = availableHeight / naturalHeight;
    const ratio = Math.min(heightRatio, widthRatio);

    // Des que le contenu depasse, on le reduit : la zone de jeu est en overflow
    // hidden, donc un debordement tolere se voit comme un rognage. Sur iPhone 16
    // Pro Max couche le contenu depassait d'environ 3%, ce qui coupait le bas des
    // cartes d'accords ; une echelle de 0,97 ne se voit pas, le rognage si.
    // La marge de 0,5% absorbe les arrondis de mesure sans laisser passer de crop
    // visible (moins de 2 px sur une zone de 370).
    const newScale = ratio >= 0.995 ? 1 : ratio;

    setScale(prev => {
      if (Math.abs(prev - newScale) > 0.005) {
        return newScale;
      }
      return prev;
    });

    // Le contenu qui tient exactement (ou qui vient d'etre reduit pour tenir) n'a
    // rien a centrer ; seul un contenu plus court que la zone se centre.
    setFits(ratio >= 1);
    // Reduit par la largeur seule : il reste de la hauteur, le contenu se centre.
    setWidthLimited(widthRatio < 1 && widthRatio <= heightRatio);
  }, [minContentWidth, minWidthScale]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateScale);
    });

    observer.observe(container);
    if (content) {
      observer.observe(content);
    }

    // Initial measurement after layout settles
    requestAnimationFrame(updateScale);

    return () => observer.disconnect();
  }, [updateScale, observeKey]);

  return { scale, fits, widthLimited, containerRef, contentRef };
};
